import {useToast} from '@kit';

import {useCharacter} from '../../character-context';
import {actionUseKey, foreignResource} from '../../model/action-list';
import type {CharacterAction} from '../../model/collect-effects';
import type {CharacterState} from '../../model/character-state';
import {normalizeDie} from '../../model/dice';
import {withJournalEntry} from '../../model/journal';

export type StatePatch = (current: CharacterState) => Partial<CharacterState>;

/** использования действия особенности и запись его применения в журнал с тостом */
export function useFeatureAction(action: CharacterAction) {
	const {character, update} = useCharacter();
	const toast = useToast();
	const useKey = actionUseKey(character, action);
	const use = useKey ? character.limitedUse(useKey) : undefined;
	const remaining = use ? use.max - use.spent : Infinity;
	const resource = action.resource ? character.resources.find(({id}) => id === action.resource) : undefined;
	/** кость, которую бросает применение: кость ресурса у Острых слов и манёвров */
	const rollDice = action.rollsResourceDie && resource?.die != null ? normalizeDie(String(resource.die)) : undefined;

	/** применение без траты использования: обмен ячеек, трата запаса */
	const record = (details: string, patch?: StatePatch) => {
		update((current) => ({
			...patch?.(current),
			...withJournalEntry(current, {kind: 'feature', title: action.title, details}),
		}));
		toast.show({intent: 'success', title: action.title, body: details});
	};

	/** тратит одно использование; patch — что ещё меняется тем же применением */
	const spend = (details?: string, patch?: StatePatch) => {
		update((current) => {
			const changes = patch?.(current) || {};
			const spentUses = changes.spentUses || current.spentUses;
			return {
				...changes,
				...(useKey ? {spentUses: {...spentUses, [useKey]: (spentUses[useKey] || 0) + 1}} : null),
				...withJournalEntry(current, {kind: 'feature', title: action.title, details}),
			};
		});
		const left = use ? `, осталось ${remaining - 1}` : '';
		const foreign = foreignResource(character, action);
		if (foreign) {
			const spent = `Потрачено: ${foreign.title}${left}`;
			toast.show({intent: 'success', title: action.title, body: details ? `${spent}. ${details}` : spent});
			return;
		}
		toast.show({intent: 'success', title: `${action.title}: применено${left}`, body: details});
	};

	return {hasUses: !!use, remaining, canUse: remaining > 0, rollDice, spend, record};
}
